import { useMemo, useState } from 'react';

import { GarmentImage } from '@/components/GarmentImage';
import { HeldBadge, HeldByLine } from '@/components/HeldBy';
import { Card, EmptyState, SectionTitle } from '@/components/PageHeading';
import { StatusBadge } from '@/components/StatusBadge';
import { useDebounced } from '@/components/useDebounced';
import { formatDate } from '@core/format/dates';
import { formatAmount } from '@core/format/money';
import { priceGarment, slabLabel, type UnitRateCard } from '@core/bookings/pricing';
import { withReference } from '@core/units/referenceNumber';
import {
  categoriesOnRail,
  freeToPick,
  visibleGarments,
  type AvailableUnit,
} from '@core/bookings/rail';

/** What one garment costs over the charged period, as the picker renders it. */
export interface PickedPrice {
  rate: number | null;
  label: string | null;
}

/**
 * The rail: what is free for these dates, filtered, counted and priced.
 *
 * <p>
 * <strong>Lifted out of `NewBookingPage` so amending a booking can use it rather than grow a second
 * one.</strong> The chips with their `free/total` counts, the search box, the three empty states and
 * the priced tile grid were ~200 lines inside that screen and are exactly what an amendment needs —
 * the clerk swapping a sherwani a week before the wedding is looking at the same rail as the one who
 * took the booking. Rebuilding it would have been the second presentation that drifts, and the
 * neglected copy is always the one used less, which is the argument `DataView` already makes against
 * a per-screen `renderCard`.
 * </p>
 *
 * <p>
 * <strong>Purely presentational, and it owns only its two filters.</strong> The search text and the
 * selected category are about looking rather than about the booking, so they live here; everything
 * that decides what is picked, what it costs and what happens next belongs to the caller. `rail.ts`
 * already held the pure part and needed nothing.
 * </p>
 *
 * <p>
 * <strong>`notice` is a slot rather than a rule</strong>, because the two callers say different
 * things above the grid for the same reason: a new booking cannot pick until somebody is in the
 * party, an amendment cannot pick until the clerk has said what they are replacing. Both are "why is
 * this greyed out", and answering it was a real defect on the first screen — a rail of disabled
 * cards reads as "none of this is available" while the chips directly above say the opposite.
 * </p>
 */
export function GarmentPicker({
  units,
  days,
  claimed,
  droppable,
  currency = '',
  title = 'Available garments',
  searching = false,
  searched = false,
  disabled = false,
  notice,
  onPick,
  onDrop,
  onChaseHold,
  toggle,
}: {
  /** Results for the current dates. The caller owns the query. */
  units: AvailableUnit[];
  /** Charged days, inclusive of both ends — what each garment's card is read against. */
  days: number;
  /** Units already spoken for on this screen, drawn with a tick and pickable again to drop. */
  claimed: Set<number>;
  /**
   * Which claimed units may be un-picked from the rail. Omitted means every one of them, which is
   * what a fresh booking wants — everything claimed there is a draft pick. An amendment passes the
   * subset it brought in, because a garment already live on the booking is claimed too and leaving
   * it clickable would give the clerk a tile that does nothing.
   */
  droppable?: Set<number> | undefined;
  currency?: string;
  title?: string;
  searching?: boolean;
  /** Whether a search has been run at all, so "none free" reads differently from "not asked yet". */
  searched?: boolean;
  /** Nothing is pickable — the caller has not been told who or what this is for yet. */
  disabled?: boolean;
  notice?: React.ReactNode;
  onPick: (unit: AvailableUnit, priced: PickedPrice) => void;
  /** Absent when a claimed garment cannot be un-picked, as on an amendment's replacement slot. */
  onDrop?: ((unit: AvailableUnit) => void) | undefined;
  /** Absent when the caller cannot manage a hold — the details still show, the button does not. */
  onChaseHold?: ((unit: AvailableUnit) => void) | undefined;
  /** A control beside the filter — the booking counter's switch back to design cards. */
  toggle?: React.ReactNode;
}) {
  const [search, setSearch] = useState('');

  // Null is "every category", which is the state the counter opens in: a clerk who knows what they
  // want types it, and one who does not browses the tree.
  const [category, setCategory] = useState<number | null>(null);

  const debounced = useDebounced(search);

  // "Sherwani 2/9" is the question a clerk asks before they ask anything else, and it is the
  // difference between a full-looking rail and a shop with one garment left for a wedding weekend.
  const categories = useMemo(() => categoriesOnRail(units, claimed), [units, claimed]);

  const visible = useMemo(
    () => visibleGarments(units, { category, query: debounced }),
    [units, debounced, category],
  );

  const freeShown = freeToPick(visible, claimed);

  /**
   * <strong>Per garment, not per design.</strong> The card arrives on the garment itself — the
   * server resolved unit → variant → item → category and reduced the surviving modifiers to one
   * percentage — so two copies of one design can, and often do, come to different money.
   *
   * Null rather than zero when nothing prices it: unfinished setup, not a freebie.
   */
  const priceOf = (card: UnitRateCard | null): PickedPrice => {
    const charge = priceGarment(card, days);

    return charge === null
      ? { rate: null, label: null }
      : { rate: charge.amount, label: slabLabel(charge) };
  };

  return (
    <Card hue="orange">
      <SectionTitle
        icon="icon icon-shirt"
        hue="orange"
        trailing={
          <div className="flex flex-wrap items-center gap-2 max-sm:w-full">
            <div className="relative w-56 max-sm:w-full">
              <i className="icon icon-search text-default pointer-events-none absolute start-2.5 top-1/2 -translate-y-1/2 text-[13px]" />
              <input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Filter by design or serial"
                className="border-border-color h-8 w-full rounded border ps-8 pe-2.5 text-[12.5px]"
              />
            </div>
            {toggle}
          </div>
        }
      >
        {title}
      </SectionTitle>

      {/* The catalogue tree as a row of chips, the template's own POS shape. A Select would hide the
          counts, and the counts are what a clerk reads first. */}
      {categories.length > 1 && (
        <div className="mb-3 flex flex-wrap gap-1.5">
          <CategoryChip
            label="All"
            free={freeToPick(units, claimed)}
            total={units.length}
            active={category === null}
            onClick={() => setCategory(null)}
          />

          {categories.map((row) => (
            <CategoryChip
              key={row.id ?? 'none'}
              label={row.name}
              free={row.free}
              total={row.total}
              active={category === row.id}
              onClick={() => setCategory(category === row.id ? null : row.id)}
            />
          ))}
        </div>
      )}

      {searched && visible.length > 0 && (
        <p className="text-default mb-3 text-[12.5px]">
          <b className="text-gray-900">{freeShown}</b> free of {visible.length} garment
          {visible.length === 1 ? '' : 's'}
          {category !== null || debounced.trim() ? ' shown' : ' on the rail for these dates'}
          {freeShown < visible.length && (
            <> — the rest are held, booked or already in this booking, as each card says.</>
          )}
        </p>
      )}

      {notice}

      {searching && (
        <p className="text-default py-8 text-center text-[13px]">Searching the rail…</p>
      )}

      {!searching && visible.length === 0 && (
        <EmptyState
          icon="icon icon-shirt"
          title={searched ? 'Nothing free for those dates' : 'Search the rail first'}
          hint={
            searched
              ? 'Try widening the possession period, or check the committed garments — one may come back in time.'
              : 'Set the dates above and search. Availability is answered for the possession period.'
          }
        />
      )}

      {!searching && visible.length > 0 && (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xxl:grid-cols-4">
          {visible.map((unit) => {
            const priced = priceOf(unit.rateCard);

            return (
              <GarmentCard
                key={unit.unitId}
                unit={unit}
                rate={priced.rate}
                rateLabel={priced.label}
                currency={currency}
                picked={claimed.has(unit.unitId)}
                disabled={disabled}
                onPick={() => onPick(unit, priced)}
                onDrop={
                  onDrop && (droppable ? droppable.has(unit.unitId) : true)
                    ? () => onDrop(unit)
                    : undefined
                }
                onChaseHold={onChaseHold ? () => onChaseHold(unit) : undefined}
              />
            );
          })}
        </div>
      )}
    </Card>
  );
}

/**
 * One category on the rail, with what is free out of what is there.
 *
 * The count is drawn as its own pill inside the chip so it reads at a glance on a busy Saturday —
 * and it stays visible on the selected chip, where the number is the thing being narrowed to.
 */
export function CategoryChip({
  label,
  free,
  total,
  active,
  onClick,
}: {
  label: string;
  free: number;
  total: number;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={`${free} free of ${total}`}
      className={`border-border-color flex items-center gap-1.5 rounded-full border px-3 py-1 text-[12px] ${
        active ? 'bg-primary border-primary text-white' : 'text-default bg-white hover:bg-light'
      }`}
    >
      <span className={active ? 'font-medium' : ''}>{label}</span>

      <span
        className={`font-mono-nums rounded-full px-1.5 text-[11px] ${
          active
            ? 'bg-white/20 text-white'
            : // Nothing free is worth seeing before the chip is clicked, not after.
              free === 0
              ? 'bg-danger-transparent text-danger'
              : 'bg-light text-default'
        }`}
      >
        {free}/{total}
      </span>
    </button>
  );
}

/**
 * One garment on the rail.
 *
 * <p>
 * <strong>A committed garment is a `<div>`, not a disabled `<button>`</strong>, and that is a fix
 * rather than a preference. Everything inside a disabled button is inert — so the holder's `tel:`
 * link could not be tapped and a "manage the hold" control could not exist at all, since a button
 * inside a button is invalid markup either way. A held card is not pickable, so it should never have
 * been a button; only the pickable one is.
 * </p>
 */
function GarmentCard({
  unit,
  rate,
  rateLabel,
  currency,
  picked,
  disabled,
  onPick,
  onDrop,
  onChaseHold,
}: {
  unit: AvailableUnit;
  /** From the slab card and the charged period — not `unit.rate`, which has no duration in it. */
  rate: number | null;
  rateLabel: string | null;
  currency: string;
  picked: boolean;
  disabled: boolean;
  onPick: () => void;
  /** Absent when a picked garment cannot be un-picked from here. */
  onDrop?: (() => void) | undefined;
  /** Absent when the caller cannot manage a hold — the details still show, the button does not. */
  onChaseHold?: (() => void) | undefined;
}) {
  const held = unit.heldBy ?? null;
  const pickable = held === null || picked;

  const body = (
    <>
      <GarmentImage url={unit.imageUrl} alt={unit.serialCode} size="lg" />

      <div className="flex items-start justify-between gap-2">
        <span className="min-w-0">
          <span className="block truncate text-[13px] font-semibold text-gray-900">
            {unit.itemName}
          </span>
          <span className="text-default block truncate text-[11.5px]">
            {unit.variantDisplayName}
          </span>
        </span>

        {picked && (
          <span className="bg-primary flex size-5 shrink-0 items-center justify-center rounded-full text-white">
            <i className="icon icon-check text-[11px]" />
          </span>
        )}
      </div>

      <span className="text-default font-mono-nums block truncate text-[11px]">
        {withReference(unit.serialCode, unit.referenceNumber)}
      </span>

      <div className="flex items-center justify-between gap-2">
        {rate === null ? (
          // Unfinished setup, not a freebie — the booking is refused until a card covers it.
          <StatusBadge tone="warning">No rate</StatusBadge>
        ) : (
          <span className="flex flex-col">
            <span className="font-mono-nums text-[13px] font-semibold text-gray-900">
              {currency}
              {formatAmount(rate)}
            </span>
            {rateLabel && <span className="text-default text-[10.5px]">{rateLabel}</span>}
          </span>
        )}

        {/* Committed garments are shown rather than hidden, with the date they free: "no, but it is
            back on the 15th" is what a counter clerk needs, and an empty list is what sends the
            customer to the shop next door. A tentative hold is drawn as its own thing, because it
            is the one a clerk can still do something about. */}
        {held &&
          (held.holder ? (
            <HeldBadge tentative={held.tentative} />
          ) : (
            <StatusBadge tone="neutral">{held.reason}</StatusBadge>
          ))}
      </div>

      {held && (
        <span className="text-default block text-[11px]">
          until {formatDate(held.untilDate)}
          {held.freeOn ? ` · free ${formatDate(held.freeOn)}` : ''}
        </span>
      )}

      {/* The name and the number, so the counter can ring the first party rather than turning the
          second one away. */}
      {held?.holder && <HeldByLine holder={held.holder} tentative={held.tentative} />}
    </>
  );

  if (pickable) {
    return (
      <button
        type="button"
        disabled={disabled || (picked && !onDrop)}
        onClick={picked ? onDrop : onPick}
        className={`pos-product-card border-border-color flex cursor-pointer flex-col gap-2 rounded-lg border bg-white p-3 text-start disabled:cursor-not-allowed disabled:opacity-60 ${picked ? 'selected' : ''}`}
      >
        {body}
      </button>
    );
  }

  return (
    <div className="pos-product-card border-border-color flex flex-col gap-2 rounded-lg border bg-white p-3 text-start opacity-90">
      {body}

      {/* The one thing a clerk can do about a garment somebody else is holding: ring them, and
          record what they said. A firm booking has no button — releasing a confirmed wedding order
          is not a thing to offer from a picker. */}
      {held?.tentative && held.holder && onChaseHold && (
        <button
          type="button"
          onClick={onChaseHold}
          disabled={disabled}
          className="border-warning text-warning hover:bg-warning-transparent flex items-center justify-center gap-1.5 rounded border px-2 py-1 text-[11.5px] disabled:cursor-not-allowed disabled:opacity-60"
        >
          <i className="icon icon-phone-call text-[12px]" />
          Chase this hold
        </button>
      )}
    </div>
  );
}
